import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { TemplatePageCategoryModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/templatePageCategory/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/templatePageCategory/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/templatePageCategory/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/templatePageCategory/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/templatePageCategory/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/templatePageCategory/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/templatePageCategory/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/templatePageCategory/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/templatePageCategory/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/templatePageCategory/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/templatePageCategory/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/templatePageCategory/DELETE_FAILURE'

export type TemplatePageCategoryModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TemplatePageCategoryModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTemplatePageCategories(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().templatePageCategories.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/template_page_categories.json', options)
      .then(({ data }: { data: TemplatePageCategoryModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_CATEGORY_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTemplatePageCategory(templatePageCategoryId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.templatePageCategories.loadedForKeys]
    const loadedIds = [...state.templatePageCategories.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (templatePageCategoryId && !loadedIds.includes(templatePageCategoryId)) loadedIds.push(templatePageCategoryId)

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/template_page_categories/${templatePageCategoryId}.json`, options)
      .then(({ data }: { data: TemplatePageCategoryModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_CATEGORY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createTemplatePageCategory(
  templatePageCategory: Partial<TemplatePageCategoryModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_page_category: templatePageCategory,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/template_page_categories.json', options, config)
      .then(({ data }: { data: TemplatePageCategoryModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_CATEGORY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateTemplatePageCategory(
  templatePageCategory: UpdateParams<TemplatePageCategoryModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      template_page_category: templatePageCategory,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_page_categories/${templatePageCategory.id}.json`, options, config)
      .then(({ data }: { data: TemplatePageCategoryModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_CATEGORY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteTemplatePageCategory(templatePageCategory: DeleteParams<TemplatePageCategoryModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/template_page_categories/${templatePageCategory.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_CATEGORY)
        dispatch(updateEntities(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TemplatePageCategoryModuleState = initialState,
  action: Action = { type: '' },
): TemplatePageCategoryModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
